/**
 * `/api/share-links/**` — REV23 (docs/specs/REV23-share-profile.md): authenticated management of
 * the public share-profile link. See `ShareLinkController`/`ShareService` for the authoritative
 * shapes. Mirrors `lib/legionProgress.ts`'s hook shape (list query + per-account mutations).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './api';
import { pushToast } from './events';

export interface ShareLinkDto {
  accountId: number;
  token: string;
  enabled: boolean;
  displayName: string;
  showLegion: boolean;
  showHexa: boolean;
  showPitched: boolean;
  showLinks: boolean;
}

export interface ShareLinkUpsertInput {
  displayName: string;
  enabled: boolean;
  showLegion: boolean;
  showHexa: boolean;
  showPitched: boolean;
  showLinks: boolean;
}

export const SHARE_LINKS_QUERY_KEY = ['share-links'] as const;

export const shareLinksQuery = {
  queryKey: SHARE_LINKS_QUERY_KEY,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<ShareLinkDto[]>('/api/share-links', signal),
};

export function useShareLinks() {
  return useQuery(shareLinksQuery);
}

function useInvalidateShareLinks() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: SHARE_LINKS_QUERY_KEY });
}

export function useUpsertShareLink() {
  const invalidate = useInvalidateShareLinks();
  return useMutation({
    mutationFn: ({ accountId, input }: { accountId: number; input: ShareLinkUpsertInput }) =>
      apiSend<ShareLinkDto>('PUT', `/api/share-links/${accountId}`, input),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not save the public profile settings.';
      pushToast(message);
    },
  });
}

/** The old token 404s immediately once this resolves — see `ShareService.rotate`. */
export function useRotateShareLink() {
  const invalidate = useInvalidateShareLinks();
  return useMutation({
    mutationFn: (accountId: number) => apiSend<ShareLinkDto>('POST', `/api/share-links/${accountId}/rotate`),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not rotate the share link.';
      pushToast(message);
    },
  });
}

export function useDeleteShareLink() {
  const invalidate = useInvalidateShareLinks();
  return useMutation({
    mutationFn: (accountId: number) => apiSend<void>('DELETE', `/api/share-links/${accountId}`),
    onSuccess: () => invalidate(),
    onError: (error) => {
      const message = error instanceof ApiError ? error.serverMessage : 'Could not remove the share link.';
      pushToast(message);
    },
  });
}
